/**
 * validation.js
 *
 * Field validation shared by the four Investment Services pages
 * (IPO, NCD, Corporate Bond, Fixed Deposit).
 *
 * The admin CRUD routes carry no request schema, so this is the only thing
 * standing between a typo and a bad row — a negative lot size, a close date
 * before the open date, a coupon rate of 900%. Rules live here rather than in
 * the pages so all four screens reject the same things.
 *
 * Returns an object keyed by field name; an empty object means the form is
 * valid.
 */

const URL_PATTERN = /^https?:\/\/[^\s]+\.[^\s]{2,}$/i;

// ── Primitive checks ───────────────────────────────────────────

const isBlank = (v) => v === undefined || v === null || String(v).trim() === '';

const asNumber = (v) => (isBlank(v) ? null : Number(v));

/** Present, numeric, and not negative. Blank passes — use `required` for that. */
function checkNumber(value, label, { min = 0, max = null, integer = false } = {}) {
  if (isBlank(value)) return '';

  const n = Number(value);
  if (Number.isNaN(n)) return `${label} must be a number`;
  if (integer && !Number.isInteger(n)) return `${label} must be a whole number`;
  if (n < min) return `${label} cannot be less than ${min}`;
  if (max !== null && n > max) return `${label} cannot be more than ${max}`;
  return '';
}

function checkPercent(value, label) {
  return checkNumber(value, label, { min: 0, max: 100 });
}

function checkUrl(value, label) {
  if (isBlank(value)) return '';
  return URL_PATTERN.test(String(value).trim()) ? '' : `${label} must be a valid URL (https://…)`;
}

/** Ordering check for a pair of dates, skipped unless both are filled. */
function checkDateOrder(earlier, later, message) {
  if (isBlank(earlier) || isBlank(later)) return '';
  return new Date(later) < new Date(earlier) ? message : '';
}

/** Ordering check for a min/max pair, skipped unless both are filled. */
function checkRange(minValue, maxValue, message) {
  if (isBlank(minValue) || isBlank(maxValue)) return '';
  return asNumber(maxValue) < asNumber(minValue) ? message : '';
}

// ── Shared rules ───────────────────────────────────────────────

function validateCommon(data) {
  const errors = {};

  const name = String(data.company_name || '').trim();
  if (!name) errors.company_name = 'Company name is required';
  else if (name.length < 2) errors.company_name = 'Company name must be at least 2 characters';
  else if (name.length > 150) errors.company_name = 'Company name must be under 150 characters';

  const website = checkUrl(data.company_website, 'Company website');
  if (website) errors.company_website = website;

  const closeBeforeStart = checkDateOrder(
    data.start_date,
    data.close_date,
    'Close date cannot be before the start date'
  );
  if (closeBeforeStart) errors.close_date = closeBeforeStart;

  const listingBeforeClose = checkDateOrder(
    data.close_date,
    data.listing_date,
    'Listing date cannot be before the close date'
  );
  if (listingBeforeClose) errors.listing_date = listingBeforeClose;

  if (!isBlank(data.rating) && String(data.rating).length > 60) {
    errors.rating = 'Rating must be under 60 characters';
  }

  if (!isBlank(data.notes) && String(data.notes).length > 2000) {
    errors.notes = 'Notes must be under 2000 characters';
  }

  return errors;
}

// ── Per-service rules ──────────────────────────────────────────

function validateIpo(data) {
  const errors = validateCommon(data);

  const minBid = checkNumber(data.min_bid, 'Min bid price');
  if (minBid) errors.min_bid = minBid;

  const maxBid = checkNumber(data.max_bid, 'Max bid price');
  if (maxBid) errors.max_bid = maxBid;

  if (!errors.max_bid) {
    const band = checkRange(data.min_bid, data.max_bid, 'Max bid cannot be lower than min bid');
    if (band) errors.max_bid = band;
  }

  const lot = checkNumber(data.lot_size, 'Lot size', { min: 1, integer: true });
  if (lot) errors.lot_size = lot;

  const faceValue = checkNumber(data.face_value, 'Face value');
  if (faceValue) errors.face_value = faceValue;

  const drhp = checkUrl(data.drhp_link, 'DRHP link');
  if (drhp) errors.drhp_link = drhp;

  return errors;
}

function validateNcd(data) {
  const errors = validateCommon(data);

  const minInv = checkNumber(data.min_investment, 'Min investment');
  if (minInv) errors.min_investment = minInv;

  const maxInv = checkNumber(data.max_investment, 'Max investment');
  if (maxInv) errors.max_investment = maxInv;

  if (!errors.max_investment) {
    const range = checkRange(
      data.min_investment,
      data.max_investment,
      'Max investment cannot be lower than min investment'
    );
    if (range) errors.max_investment = range;
  }

  const maturity = checkNumber(data.maturity_amount, 'Maturity amount');
  if (maturity) errors.maturity_amount = maturity;

  const faceValue = checkNumber(data.face_value, 'Face value');
  if (faceValue) errors.face_value = faceValue;

  const coupon = checkPercent(data.coupon_rate, 'Coupon rate');
  if (coupon) errors.coupon_rate = coupon;

  return errors;
}

function validateDeposit(data, { amountLabel = 'investment' } = {}) {
  const errors = validateCommon(data);

  const minInv = checkNumber(data.min_investment, `Min ${amountLabel}`);
  if (minInv) errors.min_investment = minInv;

  const maxInv = checkNumber(data.max_investment, `Max ${amountLabel}`);
  if (maxInv) errors.max_investment = maxInv;

  if (!errors.max_investment) {
    const range = checkRange(
      data.min_investment,
      data.max_investment,
      `Max ${amountLabel} cannot be lower than min ${amountLabel}`
    );
    if (range) errors.max_investment = range;
  }

  for (const [field, label] of [
    ['return_1year', '1 year return'],
    ['return_2year', '2 year return'],
    ['return_3year', '3 year return'],
    ['return_4year', '4 year return'],
    ['return_5year', '5 year return'],
    ['return_allyear', 'All year return'],
  ]) {
    const message = checkPercent(data[field], label);
    if (message) errors[field] = message;
  }

  return errors;
}

/**
 * @param {'IPO'|'NCD'|'CORPORATE_BOND'|'FIXED_DEPOSIT'} kind
 * @param {object} data - the page's formData
 * @returns {object} field name → message; empty when valid
 */
export function validateInvestmentForm(kind, data) {
  switch (kind) {
    case 'IPO':
      return validateIpo(data);
    case 'NCD':
      return validateNcd(data);
    case 'FIXED_DEPOSIT':
      return validateDeposit(data, { amountLabel: 'deposit' });
    case 'CORPORATE_BOND':
    default:
      return validateDeposit(data);
  }
}

export default validateInvestmentForm;
